/**
 * Session Timeout Warning System
 * WCAG 2.2.6 Compliant - Timeouts (Level AAA)
 *
 * This script monitors user activity and warns users before session timeout
 * to prevent data loss and improve accessibility.
 */

(function () {
  "use strict";

  // Configuration
  const CONFIG = {
    // Session timeout duration in milliseconds (20 hours for WCAG compliance)
    // WCAG 2.2.6 requires at least 20 hours of inactivity before timeout
    SESSION_TIMEOUT: 20 * 60 * 60 * 1000, // 20 hours

    // Warning time before timeout in milliseconds (30 minutes before)
    WARNING_TIME: 30 * 60 * 1000, // 30 minutes

    // Countdown update interval
    COUNTDOWN_INTERVAL: 1000, // 1 second

    // LocalStorage keys
    STORAGE_KEYS: {
      LAST_ACTIVITY: "wcag_last_activity_time",
      SESSION_EXTENDED: "wcag_session_extended",
    },

    // Events to track user activity
    ACTIVITY_EVENTS: [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ],
  };

  // State management
  let warningTimer = null;
  let countdownTimer = null;
  let lastActivityTime = Date.now();
  let warningShown = false;
  let modal = null;
  let countdownElement = null;
  let globalKeydownHandler = null;

  function addGlobalDismissHandlers() {
    if (globalKeydownHandler) {
      return;
    }

    globalKeydownHandler = function (e) {
      if (e.key !== "Escape") {
        return;
      }

      if (!warningShown || !modal || modal.style.display === "none") {
        return;
      }

      const continueBtn = document.getElementById("wcag-continue-btn");
      const isExpiredState =
        continueBtn &&
        typeof continueBtn.textContent === "string" &&
        continueBtn.textContent.toLowerCase().includes("refresh");

      if (isExpiredState) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      handleContinueSession();
    };

    document.addEventListener("keydown", globalKeydownHandler, true);
  }

  function removeGlobalDismissHandlers() {
    if (!globalKeydownHandler) {
      return;
    }

    document.removeEventListener("keydown", globalKeydownHandler, true);
    globalKeydownHandler = null;
  }

  /**
   * Initialize the session timeout system
   */
  function init() {
    // Don't run on checkout pages (handled by Shopify)
    if (window.location.pathname.includes("/checkouts/")) {
      return;
    }

    // Hydrate last activity from storage (or seed it) so state exists even before user interacts
    const storedLastActivity = localStorage.getItem(
      CONFIG.STORAGE_KEYS.LAST_ACTIVITY
    );
    if (storedLastActivity) {
      const parsed = parseInt(storedLastActivity, 10);
      if (!Number.isNaN(parsed)) {
        lastActivityTime = parsed;
      }
    } else {
      lastActivityTime = Date.now();
      localStorage.setItem(
        CONFIG.STORAGE_KEYS.LAST_ACTIVITY,
        lastActivityTime.toString()
      );
    }

    // Create modal if it doesn't exist
    createModal();

    // Setup activity listeners
    setupActivityListeners();

    // Start monitoring
    resetTimer();

    // Check for existing session on page load
    checkExistingSession();

    console.log("✓ WCAG Session Timeout System initialized");
  }

  /**
   * Create the warning modal HTML structure
   */
  function createModal() {
    // Check if modal already exists
    if (document.getElementById("wcag-session-warning")) {
      modal = document.getElementById("wcag-session-warning");
      countdownElement = document.getElementById("wcag-countdown");
      return;
    }

    // WCAG 1.4.13 compliant modal with SCR39 attributes
    const modalHTML = `
      <div id="wcag-session-warning" 
           class="wcag-modal" 
           role="dialog" 
           aria-labelledby="wcag-modal-title" 
           aria-describedby="wcag-modal-desc" 
           aria-modal="true" 
           style="display: none; pointer-events: auto;" 
           data-wcag-sc="1.4.13"
           data-wcag-1413-compliant="true"
           data-dismissible="true"
           data-dismiss-key="Escape"
           data-hoverable="true"
           data-persistent="true"
           tabindex="-1">
        <div class="wcag-modal-overlay" aria-hidden="true" style="pointer-events: auto;"></div>
        <div class="wcag-modal-content" style="pointer-events: auto;">
          <div class="wcag-modal-header">
            <svg class="wcag-warning-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <h2 id="wcag-modal-title" class="wcag-modal-title">Session Timeout Warning</h2>
          </div>
          
          <div class="wcag-modal-body">
            <p id="wcag-modal-desc" class="wcag-modal-description">
              You have been inactive for approximately 19.5 hours. Your session will expire soon due to extended inactivity. Any unsaved progress may be lost.
            </p>
            <p class="wcag-modal-countdown">
              Time remaining: <strong id="wcag-countdown" aria-live="polite" aria-atomic="true">30:00</strong>
            </p>
            <p class="wcag-modal-info">
              <small>Press <kbd>Escape</kbd> or click "Continue Session" to extend your session.</small>
            </p>
          </div>
          
          <div class="wcag-modal-footer">
            <button id="wcag-continue-btn" class="wcag-btn wcag-btn-primary" type="button">
              <svg class="wcag-btn-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <polyline points="9 11 12 14 22 4"/>
                <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
              </svg>
              Continue Session
            </button>
            <button id="wcag-logout-btn" class="wcag-btn wcag-btn-secondary" type="button">
              Logout Now
            </button>
          </div>
        </div>
      </div>
      
      <!-- Screen reader live region for announcements -->
      <div id="wcag-sr-announce" class="wcag-sr-only" role="status" aria-live="assertive" aria-atomic="true"></div>
    `;

    document.body.insertAdjacentHTML("beforeend", modalHTML);

    modal = document.getElementById("wcag-session-warning");
    countdownElement = document.getElementById("wcag-countdown");

    // Setup event listeners for modal buttons
    document
      .getElementById("wcag-continue-btn")
      .addEventListener("click", handleContinueSession);
    document
      .getElementById("wcag-logout-btn")
      .addEventListener("click", handleLogout);

    // Setup keyboard trap for modal
    setupModalKeyboardTrap();

    // Close modal on overlay click
    modal
      .querySelector(".wcag-modal-overlay")
      .addEventListener("click", handleContinueSession);
  }

  /**
   * Setup keyboard trap for modal accessibility
   */
  function setupModalKeyboardTrap() {
    const modalContent = modal.querySelector(".wcag-modal-content");
    const focusableElements = modalContent.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    modalContent.addEventListener("keydown", function (e) {
      if (e.key === "Escape") {
        handleContinueSession();
        return;
      }

      if (e.key === "Tab") {
        if (e.shiftKey) {
          if (document.activeElement === firstFocusable) {
            lastFocusable.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastFocusable) {
            firstFocusable.focus();
            e.preventDefault();
          }
        }
      }
    });
  }

  /**
   * Setup activity event listeners
   */
  function setupActivityListeners() {
    CONFIG.ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, handleUserActivity, { passive: true });
    });
  }

  /**
   * Handle user activity
   */
  function handleUserActivity() {
    // Don't count activity if warning is already shown
    if (warningShown) {
      return;
    }

    lastActivityTime = Date.now();
    localStorage.setItem(
      CONFIG.STORAGE_KEYS.LAST_ACTIVITY,
      lastActivityTime.toString()
    );

    // Reset timer if needed
    if (!warningTimer) {
      resetTimer();
    }
  }

  /**
   * Reset the warning timer
   */
  function resetTimer() {
    clearWarningTimer();
    clearCountdownTimer();

    const timeUntilWarning = CONFIG.SESSION_TIMEOUT - CONFIG.WARNING_TIME;

    warningTimer = setTimeout(() => {
      showWarning();
    }, timeUntilWarning);
  }

  /**
   * Show the warning modal
   */
  function showWarning() {
    if (warningShown) {
      return;
    }

    warningShown = true;
    modal.style.display = "flex";
    document.body.style.overflow = "hidden";

    // Ensure modal can be dismissed via Escape even if focus isn't inside modal content
    addGlobalDismissHandlers();

    // Announce to screen readers
    announceToScreenReader(
      "Warning: Your session will expire in 30 minutes due to extended inactivity. Please take action to continue your session."
    );

    // Focus the continue button
    setTimeout(() => {
      document.getElementById("wcag-continue-btn").focus();
    }, 100);

    // Start countdown
    startCountdown();
  }

  /**
   * Hide the warning modal
   */
  function hideWarning() {
    warningShown = false;
    modal.style.display = "none";
    document.body.style.overflow = "";

    removeGlobalDismissHandlers();

    clearCountdownTimer();
  }

  /**
   * Start the countdown timer
   */
  function startCountdown() {
    let remainingTime = CONFIG.WARNING_TIME / 1000; // in seconds

    updateCountdownDisplay(remainingTime);

    countdownTimer = setInterval(() => {
      remainingTime--;

      if (remainingTime <= 0) {
        clearCountdownTimer();
        handleSessionExpired();
        return;
      }

      updateCountdownDisplay(remainingTime);

      // Announce at specific intervals
      if (remainingTime === 60) {
        announceToScreenReader("1 minute remaining until session timeout.");
      } else if (remainingTime === 30) {
        announceToScreenReader("30 seconds remaining until session timeout.");
      }
    }, CONFIG.COUNTDOWN_INTERVAL);
  }

  /**
   * Update countdown display
   */
  function updateCountdownDisplay(seconds) {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    const formattedTime = `${minutes}:${secs.toString().padStart(2, "0")}`;
    countdownElement.textContent = formattedTime;
  }

  /**
   * Handle continue session button click
   */
  function handleContinueSession() {
    // Record session extension
    localStorage.setItem(
      CONFIG.STORAGE_KEYS.SESSION_EXTENDED,
      Date.now().toString()
    );

    // Announce to screen readers
    announceToScreenReader(
      "Session extended successfully. You can continue working."
    );

    // Reset activity time and hide warning
    lastActivityTime = Date.now();
    localStorage.setItem(
      CONFIG.STORAGE_KEYS.LAST_ACTIVITY,
      lastActivityTime.toString()
    );

    hideWarning();
    resetTimer();
  }

  /**
   * Handle logout button click
   */
  function handleLogout() {
    announceToScreenReader("Logging out...");

    // Clear storage
    localStorage.removeItem(CONFIG.STORAGE_KEYS.LAST_ACTIVITY);
    localStorage.removeItem(CONFIG.STORAGE_KEYS.SESSION_EXTENDED);

    // Redirect to logout or homepage
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      window.location.href = window.Shopify.routes.root + "account/logout";
    } else {
      window.location.href = "/account/logout";
    }
  }

  /**
   * Handle session expired
   */
  function handleSessionExpired() {
    announceToScreenReader(
      "Your session has expired. Please refresh the page to continue."
    );

    // Update modal content
    document.getElementById("wcag-modal-title").textContent = "Session Expired";
    document.getElementById("wcag-modal-desc").textContent =
      "Your session has expired due to inactivity. Any unsaved changes may have been lost.";
    countdownElement.parentElement.style.display = "none";

    // Update buttons
    const continueBtn = document.getElementById("wcag-continue-btn");
    continueBtn.textContent = "Refresh Page";
    continueBtn.onclick = () => window.location.reload();

    document.getElementById("wcag-logout-btn").style.display = "none";
  }

  /**
   * Check for existing session
   */
  function checkExistingSession() {
    const lastActivity = localStorage.getItem(
      CONFIG.STORAGE_KEYS.LAST_ACTIVITY
    );

    if (lastActivity) {
      const timeSinceLastActivity = Date.now() - parseInt(lastActivity);

      if (timeSinceLastActivity > CONFIG.SESSION_TIMEOUT) {
        // Session expired, clear storage
        localStorage.removeItem(CONFIG.STORAGE_KEYS.LAST_ACTIVITY);
        localStorage.removeItem(CONFIG.STORAGE_KEYS.SESSION_EXTENDED);
      } else if (
        timeSinceLastActivity >
        CONFIG.SESSION_TIMEOUT - CONFIG.WARNING_TIME
      ) {
        // Should show warning
        const remainingTime = CONFIG.SESSION_TIMEOUT - timeSinceLastActivity;
        setTimeout(() => {
          showWarning();
        }, Math.max(0, remainingTime - CONFIG.WARNING_TIME));
      }
    }
  }

  /**
   * Announce message to screen readers
   */
  function announceToScreenReader(message) {
    const announcer = document.getElementById("wcag-sr-announce");
    if (announcer) {
      announcer.textContent = message;
      setTimeout(() => {
        announcer.textContent = "";
      }, 1000);
    }
  }

  /**
   * Clear warning timer
   */
  function clearWarningTimer() {
    if (warningTimer) {
      clearTimeout(warningTimer);
      warningTimer = null;
    }
  }

  /**
   * Clear countdown timer
   */
  function clearCountdownTimer() {
    if (countdownTimer) {
      clearInterval(countdownTimer);
      countdownTimer = null;
    }
  }

  /**
   * Cleanup on page unload
   */
  window.addEventListener("beforeunload", () => {
    clearWarningTimer();
    clearCountdownTimer();
    removeGlobalDismissHandlers();
  });

  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Expose API for external use
  window.WCAGSessionTimeout = {
    reset: resetTimer,
    showWarning: showWarning,
    hideWarning: hideWarning,
    getLastActivity: () => lastActivityTime,
  };
})();
